import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES } from '../../i18n';
import { LANGUAGE_CHOSEN_KEY } from '../../hooks/useCountryLanguageDefault';

interface LanguageSwitcherProps {
  /** `menu` stacks the options full-width for the account menu; `toolbar` is the inline pair. */
  variant?: 'toolbar' | 'menu';
}

export function LanguageSwitcher({ variant = 'toolbar' }: LanguageSwitcherProps) {
  const { i18n, t } = useTranslation();
  const active = i18n.resolvedLanguage ?? i18n.language;

  // Marks the choice as deliberate, so the country-derived default stops applying.
  const choose = (code: string) => {
    localStorage.setItem(LANGUAGE_CHOSEN_KEY, '1');
    void i18n.changeLanguage(code);
  };

  if (variant === 'menu') {
    return (
      <div
        role="group"
        aria-label={t('language.switcher')}
        className="flex w-full rounded border border-border p-0.5"
      >
        {SUPPORTED_LANGUAGES.map((language) => {
          const selected = active === language.code;
          return (
            <button
              key={language.code}
              type="button"
              onClick={() => choose(language.code)}
              aria-pressed={selected}
              className={`flex flex-1 items-center justify-center gap-1.5 rounded px-2 py-1.5 text-sm font-semibold transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring ${
                selected
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted'
              }`}
            >
              <span aria-hidden="true">{language.flag}</span>
              <span>{language.label}</span>
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div
      role="group"
      aria-label={t('language.switcher')}
      className="hidden items-center gap-1 rounded border border-border p-0.5 sm:inline-flex"
    >
      {SUPPORTED_LANGUAGES.map((language) => {
        const selected = active === language.code;
        return (
          <button
            key={language.code}
            type="button"
            onClick={() => choose(language.code)}
            aria-pressed={selected}
            className={`rounded px-2 py-1 text-xs font-semibold transition ${
              selected
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted'
            }`}
          >
            <span aria-hidden="true">{language.flag}</span>{' '}
            <span className="uppercase">{language.code}</span>
          </button>
        );
      })}
    </div>
  );
}
